import React from 'react'
import PropTypes from 'prop-types'
import glamorous from 'glamorous'
import glamorousIcon from '../../../lib/glamorous-icon'
import SmallPayPalSvg from './svg/small-pp.svg'
import { connect } from 'transfer/utils/p2p-connect'
import * as OverpanelActions from 'transfer/actions/overpanels'
import {
  getPaymentCardNetwork,
  generateSourceLogoUrls,
  getRelevantFundingSource,
} from 'transfer/utils/funding-option-utils'
import { smallAndSmaller } from '../../../lib/styles/media-queries'
import Overpanel from 'react-components/overpanel'
import content from 'pp-react-l10n'
import { isEmpty } from 'lodash'
import { track3DS2PageImpression } from '../../utils/3ds-utils'

const i18n = content('transfer/3ds/spinner')

const MainDiv = glamorous.div({
  textAlign: 'center',
  margin: '1rem',
  display: 'block',
  justifyContent: 'center',
  [smallAndSmaller]: {
    margin: '0',
  },
  marginTop: '5rem',
})

const CardImg = glamorous.img({
  width: '6rem',
})

const LoadingImg = glamorous.img({
  width: '8rem',
})

const TitleH2 = glamorous.h2({
  fontSize: '1.7rem',
  width: '60%',
  marginLeft: '20%',
  marginBottom: '5%',
})

const LoadingH2 = glamorous.h2({
  margin: '2rem 0',
  fontSize: '1.7rem',
  minHeight: '2.5rem',
})

const SmallPayPalIcon = glamorousIcon({
  '& svg': {
    marginTop: '2.5rem',
    height: 30,
    width: 35,
  },
})

// The commented out below is the new design we will be using later
// once the other platforms implement the new design

// const spinner = css.keyframes({
//   '0%': { transform: 'rotate(0deg)' },
//   '100%': { transform: 'rotate(360deg)' },
// })

// const LoadingIcon = glamorousIcon({
//   width: '6rem',
//   height: '6rem',
//   margin: 'auto',
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'center',
//   '&::before': {
//     content: ' ',
//     position: 'absolute',
//     width: '6rem',
//     height: '6rem',
//     borderRadius: '50%',
//     border: '0.2rem solid transparent',
//     borderTopColor: '#d6f7ed',
//     borderRightColor: '#7ae6c7',
//     borderBottomColor: '#7ae6c7',
//     animation: `${spinner} .6s linear infinite`,
//   },
//   '& svg': { width: '3rem', height: '3rem' },
// })

const LOADING_MESSAGES = ['firstMessage', 'secondMessage', 'thirdMessage']

class ThreeDS2SpinnerOverpanel extends React.Component {
  static propTypes = {
    fundingOption: PropTypes.object.isRequired,
    show: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    isCFPBEnabledCountry: PropTypes.bool,
    isCardArtEnabled: PropTypes.bool,
    userAccountType: PropTypes.string,
    cdnHostName: PropTypes.string,
  }

  static defaultProps = {
    show: false,
    isCFPBEnabledCountry: false,
    isCardArtEnabled: false,
    userAccountType: '',
  }

  // Will be used to clear and set interval
  interval = null

  state = {
    messageIndex: 0,
  }

  analyticsStrings = {
    pageGroup: 'main:p2p::cards:authenticate_with_card_network',
    pageName: 'main:p2p::cards:authenticate_with_card_network::',
  }

  componentDidUpdate(prevProps) {
    const { show } = this.props
    if (show && prevProps.show !== show) {
      const { pageGroup, pageName } = this.analyticsStrings
      const { fundingOption = {} } = this.props
      if (!isEmpty(fundingOption))
        track3DS2PageImpression(pageGroup, pageName, fundingOption)
    }
  }

  componentWillUnmount() {
    this.props.dispatch(
      OverpanelActions.toggle3DS2SpinnerOverpanel({ show: false })
    )
    this.resetState()
  }
  /**
   * This function changes the loading message index based on the following requirements
   * 1. Show first message for 2 seconds
   * 2. Show second message for 4 seconds or until loading complete
   * 3. Show last message until loading is complete
   */
  changeMessageInterval = () => {
    // Reset messageIndex incase reused again before starting interval
    this.resetState()
    let { messageIndex } = this.state
    let secondsPassed = 0
    this.interval = setInterval(() => {
      secondsPassed++
      switch (secondsPassed) {
        case 1:
          messageIndex = 0
          break
        // Should show for 2 seconds, but adding additional second for
        // time it takes for overpanel to open
        case 3:
          messageIndex = 1
          break
        case 7:
          messageIndex = 2
          clearInterval(this.interval)
          break
        default:
      }
      this.setState({ messageIndex })
    }, 1000)
  }

  getCardImage = () => {
    const {
      isCFPBEnabledCountry,
      userAccountType,
      isCardArtEnabled,
    } = this.props
    const fundingSource = this.getFundingSource()
    const logoSource = generateSourceLogoUrls(
      fundingSource,
      isCFPBEnabledCountry,
      userAccountType,
      isCardArtEnabled
    )
    return logoSource ? <CardImg alt="card-logo" src={logoSource} /> : ''
  }

  getTitleMessage = () => {
    const fundingSource = this.getFundingSource()
    const network = getPaymentCardNetwork(fundingSource)
    return network ? (
      <TitleH2>{i18n('content.title', { network })}</TitleH2>
    ) : (
      ''
    )
  }

  getFundingSource = () => {
    const { fundingOption } = this.props
    if (isEmpty(fundingOption)) return {}
    const fundingSource = getRelevantFundingSource(fundingOption)
    if (isEmpty(fundingSource)) return {}
    return fundingSource
  }

  handleClose = () => {
    this.props.dispatch(
      OverpanelActions.toggle3DS2SpinnerOverpanel({ show: false })
    )

    this.resetState()
  }

  // Reset messageIndex incase reused again
  resetState = () => {
    clearInterval(this.interval)
    this.setState({ messageIndex: 0 })
  }

  // This function returns the current message content based on messageIndex
  getLoadingMessage = () => {
    const { messageIndex } = this.state
    const message = LOADING_MESSAGES[messageIndex]

    // First message will have no content only fetch content for other cases.
    const isFirstMessage = message === 'firstMessage'
    let textContent = isFirstMessage ? ' ' : i18n(`content.${message}`)

    // Fallback to handle case where trouble getting translation
    if (typeof textContent !== 'string') {
      textContent = ' '
    }
    return <LoadingH2>{textContent}</LoadingH2>
  }

  render() {
    const { show, cdnHostName } = this.props
    return (
      <Overpanel
        show={show}
        onOverpanelOpen={this.changeMessageInterval}
        // handleClose={this.handleClose}
        hideCloseButton={true}
      >
        <MainDiv data-nemo="3ds-spinner-overpanel">
          <LoadingImg
            src={`${cdnHostName}/ui-web/money-icons/partnerships/mbLoader_256_sm.gif`}
          />
          {this.getTitleMessage()}
          {this.getCardImage()}
          {this.getLoadingMessage()}
          <SmallPayPalIcon svg={SmallPayPalSvg} />
        </MainDiv>
      </Overpanel>
    )
  }
}

export default connect(({ overpanels, transaction, server }) => ({
  fundingOption: transaction.selectedFundingOption,
  show: overpanels.show3DS2SpinnerOverpanel,
  isCFPBEnabledCountry: server.isCFPBEnabledCountry,
  userAccountType: server.userAccountType,
  isCardArtEnabled: server.features && server.features.isCardArtEnabled,
  cdnHostName: server.cdnHostName,
}))(ThreeDS2SpinnerOverpanel)
